/* eslint-disable @typescript-eslint/no-shadow */
import React from 'react';

import { Box, Grid } from '@ha/dsl';

import isDayAfter from '../helpers/isDayAfter';
import isDayBefore from '../helpers/isDayBefore';

import Calendar from '../Calendar/Calendar';
import CalendarFooter from '../Calendar/CalendarFooter';
import CalendarInfo from '../Calendar/CalendarInfo';
import DateInput from '../DateInput/DateInput';

import DatePickerInput from './DatePickerInput';
import { DatePickerPopover } from '../Overlays';
import { DatePickerProps } from '../types';

const DatePicker: React.FC<DatePickerProps> = ({
  AdditionalInfo,
  CaptionSubtitle,
  clearDateButton,
  datePickerProps,
  inputLabel,
  inputProps,
  justify = 'center',
  maxDate = null,
  minDate = new Date(),
  onBlur,
  onDateChange,
  onOverlayClose,
  onOverlayOpen,
  pickerCaption,
  value,
  T,
  formatDate,
  locale,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);

  const onDatesClear = React.useCallback(() => {
    onDateChange(undefined);
  }, [onDateChange]);

  const onOverlayOpenHandler = React.useCallback(() => {
    if (onOverlayOpen) {
      onOverlayOpen();
    }
    setOpen(true);
  }, [onOverlayOpen]);
  const onOverlayCloseHandler = React.useCallback(() => {
    if (onOverlayClose) {
      onOverlayClose();
    }
    setOpen(false);
  }, [onOverlayClose]);

  const onOverlayExitHandler = React.useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const onDateChangeHandler = React.useCallback(
    (date: Date) => {
      if (onOverlayClose) {
        onOverlayClose();
      }
      onDateChange(date);
      setOpen(false);
    },
    [onDateChange, onOverlayClose],
  );

  const finalInputProps = {
    fullWidth: true,
    'data-test-locator': 'DatePicker / Input',
    ...inputProps,
    label: inputLabel ?? T('Select'),
  };

  const isDayDisabled = React.useCallback(
    (day: Date) => {
      if (minDate && isDayBefore(day, new Date(minDate))) {
        return true;
      }

      return !!(maxDate && isDayAfter(day, new Date(maxDate)));
    },
    [minDate, maxDate],
  );

  const finalDatePickerProps = {
    locale,
    ...datePickerProps,
    selectedDays: value,
    onDayClick: onDateChangeHandler,
    month: new Date(),
    modifiers: {
      start: value,
      end: value,
      disabled: isDayDisabled,
    },
  };

  return (
    <Grid container justify={justify} ref={ref}>
      <DatePickerInput
        inputProps={finalInputProps}
        onInputFocus={onOverlayOpenHandler}
        value={value}
        T={T}
        formatDate={formatDate}
      >
        {({ onInputFocus, inputProps, value }) => (
          <DateInput
            {...inputProps}
            dateValue={value}
            onInputClick={onInputFocus}
          />
        )}
      </DatePickerInput>
      <DatePickerPopover
        containerRef={ref}
        focused={null}
        from={value}
        onOverlayClose={onOverlayCloseHandler}
        onOverlayExit={onOverlayExitHandler}
        open={open}
        OverlayActions={
          <CalendarFooter
            clearDatesButton={clearDateButton ?? T('Clear')}
            onClearDates={onDatesClear}
            onOverlayClose={onOverlayCloseHandler}
            showOverlayCloseButton={false}
            showNoEndDateButton={false}
            overlayCloseButton=""
            noEndDateButton=""
            buttonProps={{}}
          />
        }
        OverlayInfo={<CalendarInfo additionalInfo={AdditionalInfo} T={T} />}
        T={T}
        formatDate={formatDate}
      >
        <Box maxWidth={350}>
          {pickerCaption}
          <Calendar
            {...finalDatePickerProps}
            captionSubtitle={CaptionSubtitle}
            usePrevMonth={false}
            moveInWindowsTooltip=""
            highlightMoveInWindows={false}
          />
        </Box>
      </DatePickerPopover>
    </Grid>
  );
};

export default DatePicker;
